import { useState, useEffect, useCallback, useRef } from 'react';
import { CLOUD_API_URL } from '../config/serverConfig';
import {
  DEFAULT_HOME_SECTION_ORDER,
  KNOWN_HOME_SECTION_IDS,
} from '../config/homeSections';

const LS_KEY = 'maestro_home_section_order';

function getToken() {
  try {
    const raw = localStorage.getItem('maestro_auth');
    if (raw) {
      const parsed = JSON.parse(raw);
      return parsed?.accessToken || null;
    }
  } catch { /* ignore */ }
  return null;
}

function readLocal() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return null;
}

function writeLocal(order) {
  try {
    if (order) {
      localStorage.setItem(LS_KEY, JSON.stringify(order));
    } else {
      localStorage.removeItem(LS_KEY);
    }
  } catch { /* ignore */ }
}

function reconcile(saved, knownIds = KNOWN_HOME_SECTION_IDS) {
  if (!saved || !Array.isArray(saved)) return null;
  const filtered = saved.filter(id => knownIds.includes(id));
  if (filtered.length === 0) return null;
  const missing = knownIds.filter(id => !filtered.includes(id));
  const result = [...filtered, ...missing];
  if (result.length === knownIds.length &&
      result.every((id, i) => id === DEFAULT_HOME_SECTION_ORDER[i])) {
    return null;
  }
  return result;
}

/** Visible sections in user order; unknown saved IDs ignored; new sections keep default slot. */
export function applyHomeSectionOrder(sections, order) {
  if (!sections.length) return sections;
  const map = new Map(sections.map(s => [s.id, s]));
  const saved = (order || []).filter(id => KNOWN_HOME_SECTION_IDS.includes(id) && map.has(id));

  if (!order || saved.length === 0) {
    return DEFAULT_HOME_SECTION_ORDER.map(id => map.get(id)).filter(Boolean);
  }

  const savedSet = new Set(saved);
  const output = saved.map(id => map.get(id));

  for (const defId of DEFAULT_HOME_SECTION_ORDER) {
    if (!map.has(defId) || savedSet.has(defId)) continue;
    const defIdx = DEFAULT_HOME_SECTION_ORDER.indexOf(defId);
    let insertAt = output.length;
    for (let i = 0; i < output.length; i++) {
      if (DEFAULT_HOME_SECTION_ORDER.indexOf(output[i].id) > defIdx) {
        insertAt = i;
        break;
      }
    }
    output.splice(insertAt, 0, map.get(defId));
  }

  return output;
}

export default function useHomeSectionOrder() {
  const [order, setOrder] = useState(() => readLocal());
  const patchTimer = useRef(null);
  const hasFetched = useRef(false);

  const fetchRemote = useCallback(() => {
    const token = getToken();
    if (!token) return Promise.resolve();
    return fetch(`${CLOUD_API_URL}/api/user/preferences`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(prefs => {
        if (!prefs) return;
        const remote = prefs.home_section_order ?? null;
        const reconciled = reconcile(remote);
        setOrder(reconciled);
        writeLocal(reconciled);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchRemote();
  }, [fetchRemote]);

  useEffect(() => {
    const handleFocus = () => { fetchRemote(); };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchRemote]);

  const persistToServer = useCallback((newOrder) => {
    if (patchTimer.current) clearTimeout(patchTimer.current);
    patchTimer.current = setTimeout(() => {
      const token = getToken();
      if (!token) return;
      fetch(`${CLOUD_API_URL}/api/user/preferences`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ home_section_order: newOrder }),
      }).catch(() => {});
    }, 400);
  }, []);

  const updateOrder = useCallback((newOrder) => {
    const resolved = reconcile(newOrder);
    setOrder(resolved);
    writeLocal(resolved);
    persistToServer(resolved);
  }, [persistToServer]);

  const pinToTop = useCallback((sectionId) => {
    if (!KNOWN_HOME_SECTION_IDS.includes(sectionId)) return;
    const base = order || DEFAULT_HOME_SECTION_ORDER;
    updateOrder([sectionId, ...base.filter(id => id !== sectionId)]);
  }, [order, updateOrder]);

  const resetOrder = useCallback(() => {
    setOrder(null);
    writeLocal(null);
    persistToServer(null);
  }, [persistToServer]);

  const applySectionOrder = useCallback((sections) => {
    return applyHomeSectionOrder(sections, order);
  }, [order]);

  return {
    order,
    hasCustomOrder: order !== null,
    pinToTop,
    resetOrder,
    applySectionOrder,
  };
}
